import { useState, useEffect, useRef } from "react"
import { useQuery } from "@tanstack/react-query"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { XIcon, CameraIcon } from "lucide-react"
import { api } from "@/lib/api"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"

interface FormState {
  code: string
  fileId: string
  name: string
  docId: string
  docTypeId: string
  address: string
  cityName: string
  postalCode: string
  stateId: string
  countryId: string
}

const EMPTY_FORM: FormState = {
  code: "",
  fileId: "",
  name: "",
  docId: "",
  docTypeId: "",
  address: "",
  cityName: "",
  postalCode: "",
  stateId: "",
  countryId: "",
}

interface Props {
  open: boolean
  onClose: () => void
  editing: any | null
  onSave: (id: string | null, data: any) => void
  isSaving: boolean
  error: string | null
  onAvatarUpload?: (id: string, file: File) => void
  onAvatarDelete?: (id: string) => void
  isAvatarSaving?: boolean
}

export function ClientFormSheet({ open, onClose, editing, onSave, isSaving, error, onAvatarUpload, onAvatarDelete, isAvatarSaving }: Props) {
  const [form, setForm]               = useState<FormState>(EMPTY_FORM)
  const [sgtCodes, setSgtCodes]       = useState<string[]>([])
  const [sgtInput, setSgtInput]       = useState("")
  const [localAvatarUrl, setLocalAvatarUrl] = useState<string | null>(null)
  const fileInputRef                  = useRef<HTMLInputElement>(null)

  // Queries for selects
  const { data: docTypeOptions = [] } = useQuery({
    queryKey: ["doc-types"],
    queryFn: async () => {
      const res = await api["doc-types"].$get({ query: {} })
      if (!res.ok) return []
      return res.json() as Promise<{ id: string; code: string; name: string }[]>
    },
    staleTime: 60000,
  })

  const { data: countryOptions = [] } = useQuery({
    queryKey: ["countries"],
    queryFn: async () => {
      const res = await api.geo.countries.$get({ query: {} })
      if (!res.ok) return []
      return res.json() as Promise<{ id: string; code: string; name: string }[]>
    },
    staleTime: 60000,
  })

  const { data: stateOptions = [] } = useQuery({
    queryKey: ["states", form.countryId],
    queryFn: async () => {
      const res = await api.geo.states.$get({ query: { countryId: form.countryId } })
      if (!res.ok) return []
      return res.json() as Promise<{ id: string; code: string; name: string }[]>
    },
    enabled: !!form.countryId,
    staleTime: 60000,
  })

  // Track which client ID is currently loaded so we don't clobber localAvatarUrl
  // when the query re-fetches after an avatar upload (editing.id stays the same).
  const prevEditingIdRef = useRef<string | null>(null)

  useEffect(() => {
    const idChanged = editing?.id !== prevEditingIdRef.current
    prevEditingIdRef.current = editing?.id ?? null

    if (editing) {
      setForm({
        code:       editing.code ?? "",
        fileId:     editing.fileId != null ? String(editing.fileId) : "",
        name:       editing.name ?? "",
        docId:      editing.docId ?? "",
        docTypeId:  editing.docTypeId ?? "",
        address:    editing.address ?? "",
        cityName:   editing.cityName ?? "",
        postalCode: editing.postalCode ?? "",
        stateId:    editing.stateId ?? "",
        countryId:  editing.countryId ?? "",
      })
      setSgtCodes(editing.sgtCodes ?? [])
      // Only reset the local preview when switching to a different client or closing.
      if (idChanged) setLocalAvatarUrl(null)
    } else {
      setForm(EMPTY_FORM)
      setSgtCodes([])
      setLocalAvatarUrl(null)
    }
    setSgtInput("")
  }, [editing, open])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    onSave(editing?.id ?? null, {
      ...form,
      sgtCodes: sgtCodes.length > 0 ? sgtCodes : null,
    })
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="sm:max-w-md overflow-y-auto flex flex-col">
        <SheetHeader>
          <SheetTitle>{editing ? "Editar cliente" : "Nuevo cliente"}</SheetTitle>
          <SheetDescription>Información del cliente para facturación y legajo.</SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4 pb-4 flex-1">
          {editing && (
            <div className="flex items-center gap-4">
              <div className="relative group">
                <Avatar className="size-16 cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                  <AvatarImage src={localAvatarUrl ?? editing.avatarUrl ?? undefined} alt={editing.name} />
                  <AvatarFallback className="text-lg">
                    {editing.name?.slice(0, 2).toUpperCase() ?? "??"}
                  </AvatarFallback>
                </Avatar>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isAvatarSaving}
                  className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  {isAvatarSaving ? (
                    <span className="size-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    <CameraIcon className="size-5 text-white" />
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (!file) return
                    setLocalAvatarUrl(URL.createObjectURL(file))
                    onAvatarUpload?.(editing.id, file)
                    e.target.value = ""
                  }}
                />
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium">Avatar del cliente</p>
                <p className="text-xs text-muted-foreground">JPG, PNG, WebP o GIF. Se sube automáticamente.</p>
                {(localAvatarUrl ?? editing.avatarUrl) && (
                  <button
                    type="button"
                    onClick={() => {
                      setLocalAvatarUrl(null)
                      onAvatarDelete?.(editing.id)
                    }}
                    disabled={isAvatarSaving}
                    className="text-xs text-destructive hover:underline text-left disabled:opacity-50"
                  >
                    Eliminar avatar
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="client-code">Código</Label>
              <Input
                id="client-code"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                maxLength={10}
                className="uppercase font-mono"
                required
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="client-name">Nombre</Label>
              <Input
                id="client-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label>Códigos SGT</Label>
            <div className="flex flex-wrap gap-1.5 items-center min-h-9 rounded-md border border-input px-2 py-1.5 focus-within:ring-1 focus-within:ring-ring">
              {sgtCodes.map((code, i) => (
                <span key={i} className="inline-flex items-center gap-1 bg-muted font-mono text-xs px-2 py-0.5 rounded-sm">
                  {code}
                  <button type="button" onClick={() => setSgtCodes(sgtCodes.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-foreground">
                    <XIcon className="size-3" />
                  </button>
                </span>
              ))}
              <input
                value={sgtInput}
                onChange={(e) => setSgtInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault()
                    const code = sgtInput.toUpperCase().trim()
                    if (code && !sgtCodes.includes(code)) setSgtCodes([...sgtCodes, code])
                    setSgtInput("")
                  }
                }}
                placeholder={sgtCodes.length === 0 ? "Escribir y Enter..." : ""}
                className="flex-1 min-w-28 bg-transparent text-xs font-mono outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Tipo Doc.</Label>
              <Select value={form.docTypeId} onValueChange={(v: string | null) => setForm(f => ({ ...f, docTypeId: v ?? "" }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar...">
                    {docTypeOptions.find(t => t.id === form.docTypeId)?.name ?? null}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {docTypeOptions.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Nro Doc.</Label>
              <Input value={form.docId} onChange={e => setForm(f => ({ ...f, docId: e.target.value }))} />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label>Dirección</Label>
            <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Ciudad</Label>
              <Input value={form.cityName} onChange={e => setForm(f => ({ ...f, cityName: e.target.value }))} />
            </div>
            <div className="grid gap-1.5">
              <Label>Cód. Postal</Label>
              <Input value={form.postalCode} onChange={e => setForm(f => ({ ...f, postalCode: e.target.value }))} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>País</Label>
              <Select value={form.countryId} onValueChange={(v: string | null) => setForm(f => ({ ...f, countryId: v ?? "", stateId: "" }))}>
                <SelectTrigger>
                  <SelectValue placeholder="País...">
                    {countryOptions.find(c => c.id === form.countryId)?.name ?? null}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {countryOptions.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Provincia/Estado</Label>
              <Select value={form.stateId} onValueChange={(v: string | null) => setForm(f => ({ ...f, stateId: v ?? "" }))} disabled={!form.countryId}>
                <SelectTrigger>
                  <SelectValue placeholder="Estado...">
                    {stateOptions.find(s => s.id === form.stateId)?.name ?? null}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {stateOptions.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label>Legajo (fileId)</Label>
            <Input value={form.fileId} onChange={e => setForm(f => ({ ...f, fileId: e.target.value }))} placeholder="Ej: 175" />
          </div>

          {error && (
            <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{error}</p>
          )}

          <SheetFooter className="px-0 mt-auto pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Guardando..." : editing ? "Guardar cambios" : "Crear cliente"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
